<template>
  <div class="form-verify-contract">
    <validation-observer ref="formVerifyContractCustomer">
      <b-form class="repeater-form">
        <div class="customer-info-section">
          <h4 class="title-content">
            {{
              $t(
                "stepGuideControl.stepVerifyInfo.formVerifyContract.titleCustomerInfo"
              )
            }}
          </h4>
          <p class="subtitle-content">
            {{ $t('stepGuideControl.stepVerifyInfo.formVerifyContract.noteEnsureInfoAccuracy') }}
          </p>

          <b-row>
            <b-col cols="12">
              <b-form-group
                class="group-input"
                data-testid="form-group-customer-type"
              >
                <div class="d-flex justify-content-between">
                  <label
                    class="form-label"
                    for="customerType"
                  >
                    {{
                      $t(
                        "stepGuideControl.stepVerifyInfo.formVerifyContract.labelCustomerType"
                      )
                    }}
                    <span class="text-danger">*</span>
                  </label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  id="customerType"
                  rules="required"
                  :name="
                    $t(
                      'stepGuideControl.stepVerifyInfo.formVerifyContract.nameCustomerType'
                    )
                  "
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <v-select
                      :value="form.customerType"
                      style="width: 100%"
                      :placeholder="
                        $t(
                          'stepGuideControl.stepVerifyInfo.formVerifyContract.placeholderCustomerType'
                        )
                      "
                      class="input-height select-full-text"
                      :disabled="hasDisabled"
                      :options="options.customerType"
                      @input="v => $emit('update', { customerType: v })"
                    >
                      <template #open-indicator="{ attributes }">
                        <span
                          v-bind="attributes"
                        ><feather-icon
                          icon="ChevronDownIcon"
                          size="20"
                        /></span>
                      </template>
                      <template #no-options>
                        {{ $t('stepGuideControl.stepVerifyInfo.formVerifyContract.textNoOptions') }}
                      </template>
                    </v-select>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              :cols="12"
              :md="isIndividual ? 12 : 6"
              :class="isBusinessOrHousehold ? 'pr-md-0' : ''"
            >
              <b-form-group
                class="group-input"
                data-testid="form-group-customer"
              >
                <div class="d-flex justify-content-between">
                  <label
                    class="form-label"
                    for="customer"
                  >
                    {{
                      $t(
                        "stepGuideControl.stepVerifyInfo.formVerifyContract.labelCustomer"
                      )
                    }}
                    <span class="text-danger">*</span>
                  </label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  id="customer"
                  rules="required|max:200"
                  :name="
                    $t(
                      'stepGuideControl.stepVerifyInfo.formVerifyContract.nameCustomer'
                    )
                  "
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-form-input
                      id="verify-customer"
                      :value="form.customer"
                      data-testid="verify-customer"
                      class="input-height"
                      :placeholder="customerPlaceholder"
                      :disabled="hasDisabled"
                      :state="errors.length > 0 ? false : null"
                      @input="v => updateField('customer', v)"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col
              v-if="isBusinessOrHousehold"
              cols="12"
              md="6"
            >
              <b-form-group
                class="group-input"
                data-testid="form-group-tax-code"
              >
                <div class="d-flex justify-content-between">
                  <label
                    class="form-label"
                    for="taxCode"
                  >
                    {{
                      $t(
                        "stepGuideControl.stepVerifyInfo.formVerifyContract.labelTaxCode"
                      )
                    }}
                    <span class="field-hint">(VD: 8082940359, 8082940359-123)</span>
                    <span class="text-danger">*</span>
                  </label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  id="taxCode"
                  rules="required|max:20"
                  :name="
                    $t(
                      'stepGuideControl.stepVerifyInfo.formVerifyContract.nameTaxCode'
                    )
                  "
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-form-input
                      id="verify-tax-code"
                      :value="form.taxCode"
                      data-testid="verify-tax-code"
                      class="input-height"
                      :placeholder="
                        $t(
                          'stepGuideControl.stepVerifyInfo.formVerifyContract.placeholderTaxCode'
                        )
                      "
                      :disabled="hasDisabled"
                      :state="errors.length > 0 ? false : null"
                      @input="v => updateField('taxCode', v)"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row v-if="isIndividual">
            <b-col
              cols="12"
              md="6"
              class="pr-md-0"
            >
              <b-form-group
                class="group-input"
                data-testid="form-group-date-of-birth"
              >
                <div class="d-flex justify-content-between">
                  <label
                    class="form-label"
                    for="dateOfBirth"
                  >
                    {{
                      $t(
                        "stepGuideControl.stepVerifyInfo.formVerifyContract.labelDateOfBirth"
                      )
                    }}
                  </label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  id="dateOfBirth"
                  :name="
                    $t(
                      'stepGuideControl.stepVerifyInfo.formVerifyContract.nameDateOfBirth'
                    )
                  "
                >
                  <flat-pickr
                    :value="form.dateOfBirth"
                    :class="[
                      'flatpickr-custom',
                      'form-control',
                      'input-height',
                      { 'is-invalid': errors.length > 0 }
                    ]"
                    :config="datePickerConfig"
                    :disabled="hasDisabled"
                    :style="{ backgroundColor: hasDisabled ? '#efefef' : 'white' }"
                    :placeholder="
                      $t(
                        'stepGuideControl.stepVerifyInfo.formVerifyContract.placeholderDateOfBirth'
                      )
                    "
                    @input="v => $emit('update', { dateOfBirth: v })"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                class="group-input"
                data-testid="form-group-gender"
              >
                <div class="d-flex justify-content-between">
                  <label
                    class="form-label"
                    for="gender"
                  >
                    {{
                      $t(
                        "stepGuideControl.stepVerifyInfo.formVerifyContract.labelGender"
                      )
                    }}
                  </label>
                </div>
                <validation-provider
                  #default="{ errors, validate: validateGender }"
                  id="gender"
                  :name="
                    $t(
                      'stepGuideControl.stepVerifyInfo.formVerifyContract.nameGender'
                    )
                  "
                >
                  <div
                    class="custom-radio-group"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <label
                      v-for="option in options.gender"
                      :key="option.value"
                      class="custom-radio-label"
                      :class="{ 'custom-radio-label--disabled': hasDisabled }"
                    >
                      <input
                        v-model="genderValue"
                        type="radio"
                        :value="option.value"
                        :disabled="hasDisabled"
                        name="gender"
                        class="custom-radio-input"
                        @change="() => { this.$nextTick(() => validateGender(option.value)) }"
                      >
                      <div class="custom-radio-border">
                        <div class="custom-radio-dot" />
                      </div>
                      <span class="custom-radio-text">{{ option.text }}</span>
                    </label>
                  </div>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              md="6"
              class="pr-md-0"
            >
              <b-form-group
                class="group-input"
                data-testid="form-group-id-type"
              >
                <div class="d-flex justify-content-between">
                  <label
                    class="form-label"
                    for="idType"
                  >
                    {{
                      $t(
                        "stepGuideControl.stepVerifyInfo.formVerifyContract.labelIdType"
                      )
                    }}
                    <span class="text-danger">*</span>
                  </label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  id="idType"
                  rules="required"
                  :name="
                    $t(
                      'stepGuideControl.stepVerifyInfo.formVerifyContract.nameIdType'
                    )
                  "
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <v-select
                      :value="form.idType"
                      style="width: 100%"
                      :placeholder="
                        $t(
                          'stepGuideControl.stepVerifyInfo.formVerifyContract.placeholderIdType'
                        )
                      "
                      class="input-height select-full-text"
                      :disabled="hasDisabled"
                      :options="options.idType"
                      @input="v => $emit('update', { idType: v })"
                    >
                      <template #open-indicator="{ attributes }">
                        <span
                          v-bind="attributes"
                        ><feather-icon
                          icon="ChevronDownIcon"
                          size="20"
                        /></span>
                      </template>
                      <template #no-options>
                        {{ $t('stepGuideControl.stepVerifyInfo.formVerifyContract.textNoOptions') }}
                      </template>
                    </v-select>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                class="group-input"
                data-testid="form-group-id-number"
              >
                <div class="d-flex justify-content-between">
                  <label
                    class="form-label"
                    for="idNumber"
                  >
                    {{
                      $t(
                        "stepGuideControl.stepVerifyInfo.formVerifyContract.labelIdNumber"
                      )
                    }}
                    <span
                      v-if="isIndividual"
                      class="field-hint"
                    >
                      {{ $t('stepGuideControl.stepVerifyInfo.formVerifyContract.labelIdNumberIndividualHint') }}
                    </span>
                    <span
                      v-if="isBusinessOrHousehold"
                      class="field-hint"
                    >
                      {{ $t('stepGuideControl.stepVerifyInfo.formVerifyContract.labelIdNumberBusinessHint') }}
                    </span>
                    <span class="text-danger">*</span>
                  </label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  id="idNumber"
                  rules="required|max:20|idNumber"
                  :name="
                    $t(
                      'stepGuideControl.stepVerifyInfo.formVerifyContract.nameIdNumber'
                    )
                  "
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-form-input
                      id="verify-id-number"
                      :value="form.idNumber"
                      data-testid="verify-id-number"
                      class="input-height"
                      :placeholder="
                        isIndividual
                          ? $t('stepGuideControl.stepVerifyInfo.formVerifyContract.placeholderIdNumberIndividual')
                          : isBusinessOrHousehold
                            ? $t('stepGuideControl.stepVerifyInfo.formVerifyContract.placeholderIdNumberBusiness')
                            : $t('stepGuideControl.stepVerifyInfo.formVerifyContract.placeholderIdNumber')
                      "
                      :disabled="hasDisabled"
                      :state="errors.length > 0 ? false : null"
                      @input="v => updateField('idNumber', v)"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              md="6"
              class="pr-md-0"
            >
              <b-form-group
                class="group-input"
                data-testid="form-group-issue-date"
              >
                <div class="d-flex justify-content-between">
                  <label
class="form-label"
for="issueDate"
>
                    {{
                      $t(
                        "stepGuideControl.stepVerifyInfo.formVerifyContract.labelIssueDate"
                      )
                    }}
                    <span class="text-danger">*</span>
                  </label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  id="issueDate"
                  rules="required"
                  :name="
                    $t(
                      'stepGuideControl.stepVerifyInfo.formVerifyContract.nameIssueDate'
                    )
                  "
                >
                  <flat-pickr
                    :value="form.issueDate"
                    :class="[
                      'flatpickr-custom',
                      'form-control',
                      'input-height',
                      { 'is-invalid': errors.length > 0 }
                    ]"
                    :config="datePickerConfig"
                    :disabled="hasDisabled"
                    :style="{ backgroundColor: hasDisabled ? '#efefef' : 'white' }"
                    :placeholder="
                      $t(
                        'stepGuideControl.stepVerifyInfo.formVerifyContract.placeholderIssueDate'
                      )
                    "
                    @input="v => $emit('update', { issueDate: v })"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col
cols="12"
md="6"
>
              <b-form-group
                class="group-input"
                data-testid="form-group-issue-place"
              >
                <div class="d-flex justify-content-between">
                  <label
class="form-label"
for="issuePlace"
>
                    {{
                      $t(
                        "stepGuideControl.stepVerifyInfo.formVerifyContract.labelIssuePlace"
                      )
                    }}
                    <span class="text-danger">*</span>
                  </label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  id="issuePlace"
                  rules="required|max:100"
                  :name="
                    $t(
                      'stepGuideControl.stepVerifyInfo.formVerifyContract.nameIssuePlace'
                    )
                  "
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-form-input
                      id="verify-issue-place"
                      :value="form.issuePlace"
                      data-testid="verify-issue-place"
                      class="input-height"
                      :placeholder="
                        $t(
                          'stepGuideControl.stepVerifyInfo.formVerifyContract.placeholderIssuePlace'
                        )
                      "
                      :disabled="hasDisabled"
                      :state="errors.length > 0 ? false : null"
                      @input="v => updateField('issuePlace', v)"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <!-- Thông tin người đại diện -->
        <div
          v-if="isBusinessOrHousehold"
          class="representative-info-section"
        >
          <h4 class="title-content">
            {{
              $t(
                "stepGuideControl.stepVerifyInfo.formVerifyContract.titleRepresentativeInfo"
              )
            }}
          </h4>
          <b-row>
            <b-col
              cols="12"
              md="6"
              class="pr-md-0"
            >
              <b-form-group
                class="group-input"
                data-testid="form-group-representative"
              >
                <div class="d-flex justify-content-between">
                  <label
                    class="form-label"
                    for="representative"
                  >
                    {{
                      $t(
                        "stepGuideControl.stepVerifyInfo.formVerifyContract.labelRepresentative"
                      )
                    }}
                    <span class="text-danger">*</span>
                  </label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  id="representative"
                  rules="required|max:200"
                  :name="
                    $t(
                      'stepGuideControl.stepVerifyInfo.formVerifyContract.nameRepresentative'
                    )
                  "
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-form-input
                      id="verify-representative"
                      :value="form.representative"
                      data-testid="verify-representative"
                      class="input-height"
                      :placeholder="
                        $t(
                          'stepGuideControl.stepVerifyInfo.formVerifyContract.placeholderRepresentative'
                        )
                      "
                      :disabled="hasDisabled"
                      :state="errors.length > 0 ? false : null"
                      @input="v => updateField('representative', v)"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                class="group-input"
                data-testid="form-group-position"
              >
                <div class="d-flex justify-content-between">
                  <label
                    class="form-label"
                    for="position"
                  >
                    {{
                      $t(
                        "stepGuideControl.stepVerifyInfo.formVerifyContract.labelPosition"
                      )
                    }}
                    <span class="text-danger">*</span>
                  </label>
                </div>
                <validation-provider
                  #default="{ errors }"
                  id="position"
                  rules="required|max:200"
                  :name="
                    $t(
                      'stepGuideControl.stepVerifyInfo.formVerifyContract.namePosition'
                    )
                  "
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-form-input
                      id="verify-position"
                      :value="form.position"
                      data-testid="verify-position"
                      class="input-height"
                      :placeholder="
                        $t(
                          'stepGuideControl.stepVerifyInfo.formVerifyContract.placeholderPosition'
                        )
                      "
                      :disabled="hasDisabled"
                      :state="errors.length > 0 ? false : null"
                      @input="v => updateField('position', v)"
                    />
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <div class="btn-control btn-control--group">
          <b-button
            class="btn-back-inline"
            variant="link"
            @click="$emit('back')"
          >
            <div class="d-flex align-content-center">
              <feather-icon
                icon="ChevronLeftIcon"
                size="16"
              />
            {{ $t("stepGuideControl.stepVerifyInfo.formVerifyContract.textBack") }}
            </div>
          </b-button>
          <btn-loading
            type="button"
            variant-convert="btn-submit"
            pill
            @click.prevent="$emit('next')"
          >

            <div class="d-flex align-content-center">
              <span>{{ $t("stepGuideControl.stepVerifyInfo.formVerifyContract.textNext") }}</span>
              <feather-icon
                icon="ChevronRightIcon"
                size="16"
              />
            </div>
          </btn-loading>
        </div>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import '@validations'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BButton,
  BFormGroup,
  BForm,
  BFormInput,
  BInputGroup,
  BRow,
  BCol,
} from 'bootstrap-vue'
import BtnLoading from '@/layouts/components/BtnLoading.vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import { Vietnamese } from 'flatpickr/dist/l10n/vn'
import { CUSTOMER_TYPE } from '@/constants/customerType'
import { trimValue } from '@/@core/utils/utils'

export default {
  name: 'ContractInfoVerificationStep2',

  components: {
    BButton,
    BFormGroup,
    BForm,
    BFormInput,
    BInputGroup,
    BRow,
    BCol,
    BtnLoading,
    vSelect,
    flatPickr,
    ValidationProvider,
    ValidationObserver,
  },

  props: {
    form: { type: Object, required: true },
    options: { type: Object, required: true },
    hasDisabled: { type: Boolean, default: false },
  },

  computed: {
    isIndividual() {
      return this.form.customerType?.value === CUSTOMER_TYPE.INDIVIDUAL
    },
    isBusiness() {
      return this.form.customerType?.value === CUSTOMER_TYPE.BUSINESS
    },
    isHousehold() {
      return this.form.customerType?.value === CUSTOMER_TYPE.HOUSEHOLD
    },
    isBusinessOrHousehold() {
      return this.isBusiness || this.isHousehold
    },
    customerPlaceholder() {
      const key = 'stepGuideControl.stepVerifyInfo.formVerifyContract'
      if (this.isIndividual) return this.$t(`${key}.placeholderCustomerIndividual`)
      if (this.isBusiness) return this.$t(`${key}.placeholderCustomerBusiness`)
      if (this.isHousehold) return this.$t(`${key}.placeholderCustomerHousehold`)
      return this.$t(`${key}.placeholderCustomer`)
    },
    genderValue: {
      get() {
        return this.form.gender
      },
      set(value) {
        this.$emit('update', { gender: value })
      },
    },
    datePickerConfig() {
      return {
        allowInput: true,
        dateFormat: 'd/m/Y',
        maxDate: new Date(),
        locale: Vietnamese,
      }
    },
  },

  methods: {
    validate() {
      return this.$refs.formVerifyContractCustomer.validate()
    },
    updateField(field, value) {
      this.$emit('update', { [field]: trimValue(value) })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

$font-size-section: 18px;
$font-size-body: 14px;
$font-size-caption: 13px;

::v-deep .select-full-text {
  .vs__selected,
  .vs__selected-options {
    max-width: 100%;
    overflow: visible;
    text-overflow: unset;
    white-space: normal;
  }
}

.customer-info-section {
  margin-bottom: 20px;

  .title-content {
    font-size: 18px;
    font-weight: 600;
    color: #16213e;
    margin: 0 0 8px 0;
    line-height: 1.4;
  }

  .subtitle-content {
    font-size: 14px;
    line-height: 1.5;
    color: #6b7280;
    margin: 0 0 24px 0;
  }
}

.representative-info-section {
  margin-bottom: 20px;

  .title-content {
    font-size: 18px;
    font-weight: 600;
    color: #16213e;
    margin: 0 0 8px 0;
    line-height: 1.4;
  }
}

.btn-control {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;

  &--group {
    justify-content: flex-end;
    gap: 8px;
  }
}
.custom-radio-group {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.custom-radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  user-select: none;

  &--disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.custom-radio-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;

  &:checked ~ .custom-radio-border {
    border-color: #4cb375;

    .custom-radio-dot {
      opacity: 1;
    }
  }

  &:disabled {
    cursor: not-allowed;
  }

  &:disabled ~ .custom-radio-border {
    border-color: #d9d9d9;
    cursor: not-allowed;
  }

  &:disabled:checked ~ .custom-radio-border {
    border-color: #4cb375;

    .custom-radio-dot {
      opacity: 1;
    }
  }
}

.custom-radio-border {
  width: 20px;
  height: 20px;
  border: 2px solid #d9d9d9;
  border-radius: 50%;
  margin-right: 8px;
  position: relative;
  flex-shrink: 0;
  transition: border-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #4cb375;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.custom-radio-text {
  font-size: 14px;
  color: #16213e;
  line-height: 1.5;
}

.custom-radio-group.is-invalid {
  .custom-radio-border {
    border-color: #e11c4e;
  }
}

.field-hint {
  font-size: 11px;
  color: #6b7280;
  font-weight: 400;

  @media(max-width: 767px) {
    font-size: 10px;
  }
}
</style>

<style lang="scss">
  .flatpickr-current-month {
    font-size: 120% !important;
  }
</style>
